import React, { useEffect, useState } from "react";
import plans from "./data/data";
import { HiCheck } from "react-icons/hi2";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import accounting from "accounting";
import GetExchangeRate from "../../apis/GetExchangeRate";
import MainLayout from "../../layouts/MainLayOut";

function Pricing() {
  const [INRtoUSD, setINRtoUSD] = useState(false);
  const [rates, setRates] = useState(1);
  const [selectMonth, setSelectMonth] = useState("1");

  useEffect(() => {
    const getRate = async () => {
      const usd = parseFloat(localStorage.getItem("USD"));
      const inr = parseFloat(localStorage.getItem("INR"));

      if (usd > 0 && inr > 0) {
        setRates(INRtoUSD ? inr : usd);
        return;
      }

      const rate = await GetExchangeRate();
      if (rate) {
        setRates(INRtoUSD ? rate.INR : rate.USD);
        localStorage.setItem("USD", rate.USD);
        localStorage.setItem("INR", rate.INR);
      }
    };

    getRate();
  }, [INRtoUSD]);

  useEffect(() => {
    const savedMonth = localStorage.getItem("mon");
    if (savedMonth) {
      setSelectMonth(savedMonth);
    } else {
      localStorage.setItem("mon", "1");
    }
  }, []);

  const handlePlanSelection = (planName) => {
    if (!planName) return;
    localStorage.setItem("renderplan", "1");
    localStorage.setItem("userSelectedPlan", planName);
    localStorage.setItem("mon", selectMonth);
    localStorage.setItem("selectedCurrency", INRtoUSD ? "INR" : "USD");
  };

  const handleSelectMonth = (val) => {
    setSelectMonth(val);
    localStorage.setItem("mon", val);
  };

  return (
    <MainLayout>
      <div className="w-full h-fit bg-gradient-to-b relative from-slate-950 via-slate-800  to-slate-950 text-white  relative">
        {/* Currency Toggle */}
        <div className="flex justify-end p-6">
          <label className="inline-flex items-center cursor-pointer select-none relative">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={INRtoUSD}
              onChange={() => setINRtoUSD(!INRtoUSD)}
            />
            <div className="w-14 h-7 bg-gray-500 rounded-full relative transition-colors duration-300 peer-checked:bg-darkprimary peer-focus:ring-2 peer-focus:ring-lightprimary scale-125" />
            <span className="absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300 translate-x-0 peer-checked:translate-x-7" />
            <span className="ml-4 font-semibold text-gray-100 text-lg">
              {INRtoUSD ? "INR" : "USD"}
            </span>
          </label>
        </div>
        {/* Billing Period Selection */}
        <div className="flex justify-center p-m sm:p-4 mb-4">
          <motion.div
            className="rounded-3xl grid grid-cols-2 sm:grid-cols-4 bg-white text-gray-900 shadow-lg border border-gray-300 overflow-hidden font-semibold text-lg cursor-pointer select-none"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}>
            {["Monthly", "Quarterly", "Semi-Annual", "Annual"].map(
              (label, idx) => {
                const value = ["1", "3", "6", "12"][idx];
                return (
                  <motion.div
                    key={value}
                    onClick={() => handleSelectMonth(value)}
                    className={`p-5 text-center transition-colors duration-300 ${
                      selectMonth === value
                        ? "bg-primary text-white font-semibold"
                        : "hover:bg-gray-100"
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}>
                    {label}
                  </motion.div>
                );
              }
            )}
          </motion.div>
        </div>
        {/* Pricing Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 px-6 xl:px-[10vw] pb-16">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              className="w-full transition-all duration-300">
              <Link
                to="/signup"
                onClick={() => handlePlanSelection(plan.name)}
                className="block w-full no-underline">
                <div className="flex flex-col justify-between h-full min-h-[500px] rounded-xl bg-gradient-to-b from-slate-800 to-slate-900 border border-slate-600 p-6 shadow-md hover:shadow-2xl hover:border-teal-500 transition duration-300 ease-in-out">
                  <Card
                    plan={plan}
                    INRtoUSD={INRtoUSD}
                    rates={rates}
                    selectMonth={selectMonth}
                  />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </MainLayout>
  );
}

export default Pricing;

const Card = ({ plan, rates, INRtoUSD, selectMonth }) => {
  const isFreePlan = plan.name.toLowerCase() === "free";
  const price = plan.priceoffer?.[selectMonth] || {
    launchOffer: 0,
    regular: 0,
  };

  const billingLabel =
    selectMonth === "3"
      ? "Quarterly"
      : selectMonth === "6"
      ? "Semi-Annually"
      : selectMonth === "12"
      ? "Annually"
      : "Monthly";

  const savings =
    price.regular > 0
      ? Math.round(((price.regular - price.launchOffer) / price.regular) * 100)
      : 0;

  const formattedPrice = accounting.formatNumber(
    Math.floor(INRtoUSD ? price.launchOffer * rates : price.launchOffer)
  );

  return (
    <div className="flex flex-col justify-between h-full">
      <div className="flex justify-between items-center gap-2 mb-4">
        <h3 className="text-3xl font-extrabold tracking-tight text-white">
          {plan.name}
        </h3>
        {!isFreePlan && savings > 0 && (
          <div className="bg-gradient-to-r from-pink-500 to-red-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md select-none">
            Limited Time Offer
          </div>
        )}
      </div>

      <div className="mb-6">
        <div className="text-4xl font-bold text-teal-400 flex items-baseline gap-1">
          <span className="text-2xl font-normal">{INRtoUSD ? "₹" : "$"}</span>
          {formattedPrice}
          <span className="text-sm text-slate-400 ml-1 font-medium">
            /{billingLabel}
          </span>
        </div>
        {/* Uncomment to show regular price line-through
        {savings > 0 && (
          <p className="mt-1 text-sm text-slate-400 line-through">
            {INRtoUSD ? "₹" : "$"}
            {accounting.formatNumber(
              Math.floor(INRtoUSD ? price.regular * rates : price.regular)
            )}
          </p>
        )} */}
      </div>

      <p className="mb-6 text-sm text-slate-300 font-medium border-t border-slate-600 pt-3">
        {isFreePlan
          ? "Ideal for testing and evaluation."
          : plan.name === "Standard"
          ? "Perfect for small websites and startups."
          : "Designed for large websites and developers."}
      </p>

      <ul className="space-y-4 text-base font-medium">
        <li className="flex items-center gap-3">
          <HiCheck className="w-5 h-5 text-teal-400" />
          {accounting.formatNumber(
            plan.monthly_messages * parseInt(selectMonth)
          )}{" "}
          message credits
        </li>
        <li className="flex items-center gap-3">
          <HiCheck className="w-5 h-5 text-teal-400" />
          {plan.max_chatbots} chatbots
        </li>
        <li className="flex items-center gap-3">
          <HiCheck className="w-5 h-5 text-teal-400" />
          {accounting.formatNumber(plan.links_to_crawl)} links to train on
        </li>
        <li className="flex items-center gap-3">
          <HiCheck className="w-5 h-5 text-teal-400" />
          Website, web links, PDF, Text files supported
        </li>
        <li className="flex items-center gap-3">
          <HiCheck className="w-5 h-5 text-teal-400" />
          Instant ChatBot testing (Playground)
        </li>
      </ul>
    </div>
  );
};
